import { paidApiLimitMessage } from './paidApiLimit.js'

export function budgetNotify(type, message, title) {
  const titles = { error: 'Erreur', success: 'Succès', warning: 'Attention', info: 'Information' }
  return {
    type,
    title: title || titles[type] || 'Information',
    message,
    showToast: true,
  }
}

export function apiErrorMessage(error, fallback) {
  const limited = paidApiLimitMessage(error)
  if (limited) return limited
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg
  if (error?.message) return error.message
  return fallback
}

export function interpretOptimizeResponse(result) {
  if (!result || result.error || !Array.isArray(result.scenarios) || result.scenarios.length === 0) {
    return {
      ok: false,
      message: result?.error || "Aucun scénario n'a été généré",
    }
  }
  return {
    ok: true,
    result,
    unreachable: result.target_reachable === false,
    alreadyUnder: result.already_under_target === true,
  }
}

export function resultModelLabel(result) {
  if (result?.model_option === 'economical' || result?.ai_model === 'gpt-4o-mini') {
    return 'Dimensional Pioumon Frugal 12B'
  }
  if (result?.model_option === 'high_quality' || result?.ai_model === 'gpt-4o') {
    return 'Dimensional Pioumon Interpreter 76B'
  }
  return 'Dimensional Pioumon'
}

export function scenarioIdToApply(selectedScenario) {
  const id = selectedScenario?.id
  if (!id) {
    return { ok: false, message: 'Identifiant de scénario manquant. Relancez l’optimisation.' }
  }
  return { ok: true, id }
}

export function successNotificationFor(outcome) {
  if (outcome.unreachable) {
    return budgetNotify(
      'warning',
      "Les scénarios ont été générés, mais aucun n'atteint le seuil avec les articles existants.",
    )
  }
  if (outcome.alreadyUnder) {
    return budgetNotify(
      'success',
      'Le panier est déjà sous le seuil. Trois pistes d’amélioration ont été proposées.',
    )
  }
  return budgetNotify('success', 'Optimisation terminée. Trois scénarios ont été générés.')
}

export function formatPercentFr(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '—'
  return `${numeric.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}\u00a0%`
}

export function scenarioTitle(type) {
  if (type === 'conservative') return 'Dogmatique'
  if (type === 'balanced') return 'Parcimonieuse'
  if (type === 'aggressive') return 'Austère'
  return type || 'Scénario'
}

export function actionLabel(action, suggestedQuantity, currentQuantity) {
  if (action === 'keep') return 'Conserver'
  if (action === 'remove') return 'Retirer'
  if (action === 'reduce_quantity') {
    if (currentQuantity != null && suggestedQuantity != null) {
      return `Réduire la quantité : ${currentQuantity} \u2192 ${suggestedQuantity}`
    }
    if (suggestedQuantity != null) {
      return `Réduire la quantité : ${suggestedQuantity}`
    }
    return 'Réduire la quantité'
  }
  if (action === 'replace') return 'Remplacer'
  return action || ''
}

export function priorityLabel(priority) {
  if (priority === 'high') return 'Priorité haute'
  if (priority === 'medium') return 'Priorité moyenne'
  if (priority === 'low') return 'Priorité basse'
  return ''
}

export function selectableSuggestionIds(scenario) {
  return (scenario?.suggestions || [])
    .filter((row) => row && row.action !== 'keep' && row.item_id)
    .map((row) => row.item_id)
}

export function applySelectionHint(selectedCount, actionableCount) {
  if (!actionableCount) {
    return 'Aucune action à appliquer : les articles sont à conserver.'
  }
  if (!selectedCount) {
    return 'Aucune case cochée : toutes les actions proposées (retrait ou réduction) seront appliquées.'
  }
  const selected = `${selectedCount} action${selectedCount > 1 ? 's' : ''}`
  return `${selected} sur ${actionableCount} seront appliquées. Les articles à conserver ne sont pas cochables.`
}

export function allActionableSelected(selectedSet, scenario) {
  const ids = selectableSuggestionIds(scenario)
  if (!ids.length) return false
  return ids.every((id) => selectedSet?.has(id))
}

export const LANGUAGE_UNAVAILABLE_FR = 'Commentaire indisponible en français.'
export const ORIGINAL_LOST_FR = "L'explication originale n'a pas été conservée. Relancez l'analyse pour obtenir de nouveaux commentaires. Aucune justification n'a été reconstituée."
export const TRANSLATION_FAILED_FR = "La traduction des commentaires a échoué. Vous pouvez réessayer uniquement la traduction, sans relancer l'optimisation."
export const TRANSLATION_UNAVAILABLE_SHORT_FR = 'Traduction indisponible'

const EN_STRONG_WORDS = new Set([
  'the', 'this', 'that', 'these', 'those', 'because', 'should', 'would',
  'could', 'which', 'their', 'there', 'them', 'instead', 'cheaper',
  'expensive', 'unnecessary', 'recommended', 'consider', 'while', 'itself',
  'into', 'about', 'from', 'with', 'without', 'essential', 'optional',
  'useful', 'pairing', 'accessory', 'accessories',
  'processor', 'motherboard', 'duplicate', 'duplicates', 'summary', 'warning',
  'overall', 'balanced', 'conservative', 'aggressive', 'check', 'first',
  'work', 'together', 'same', 'save', 'good', 'choice', 'required', 'needed',
  'retain', 'buy', 'purchase', 'provides', 'offers',
])
const EN_WEAK_WORDS = new Set([
  'item', 'items', 'keep', 'remove', 'budget', 'savings', 'reduce',
  'quantity', 'replacement', 'and', 'for', 'are', 'not', 'you', 'your',
  'will', 'can', 'may', 'than', 'more', 'less',
])
const FR_FUNCTION_WORDS = new Set([
  'le', 'la', 'les', 'des', 'une', 'un', 'du', 'au', 'aux', 'ce', 'ces',
  'est', 'sont', 'pour', 'avec', 'sans', 'dans', 'sur', 'cette', 'cet',
  'qui', 'que', 'pas', 'plus', 'moins', 'article', 'articles', 'panier',
  'conserver', 'retirer', 'reduire', 'quantite', 'recommandation', 'car',
  'donc', 'deja', 'egalement', 'entre', 'aussi', 'mais', 'comme', 'tout',
  'toute', 'ainsi', 'selon', 'lorsque', 'puisque', 'dont', 'et', 'ou',
  'ne', 'ni', 'se', 'en', 'il', 'elle', 'nous', 'vous', 'par',
])

function foldAscii(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function proseTokens(text) {
  const folded = foldAscii(text)
  return folded.match(/[a-z]{2,}/g) || []
}

export function isUnavailablePlaceholder(text) {
  return String(text || '').trim() === LANGUAGE_UNAVAILABLE_FR
}

export function looksLikeEnglishProse(text) {
  if (isUnavailablePlaceholder(text)) return false
  const words = proseTokens(text)
  if (!words.length) return false
  const frenchHits = words.filter((word) => FR_FUNCTION_WORDS.has(word)).length
  const englishStrong = words.filter((word) => EN_STRONG_WORDS.has(word)).length
  const englishAll = englishStrong + words.filter((word) => EN_WEAK_WORDS.has(word)).length
  if (frenchHits >= 1 && englishStrong === 0) return false
  if (frenchHits > englishStrong) return false
  if (frenchHits === 0 && englishAll >= 1) return true
  if (englishStrong > frenchHits) return true
  return false
}

export function resolveExplanatoryText(text, source) {
  const display = String(text || '').trim()
  const original = String(source || '').trim()
  const lost = isUnavailablePlaceholder(display) && (!original || isUnavailablePlaceholder(original))
  if (lost) {
    return { text: TRANSLATION_UNAVAILABLE_SHORT_FR, kind: 'lost', source: '' }
  }
  const shown = (!display || isUnavailablePlaceholder(display)) ? original : display
  if (looksLikeEnglishProse(shown)) {
    return { text: TRANSLATION_UNAVAILABLE_SHORT_FR, kind: 'pending', source: shown }
  }
  return { text: shown, kind: 'ok', source: original && original !== shown ? original : '' }
}

export function displayExplanatoryText(text, source) {
  return resolveExplanatoryText(text, source).text
}

function explanatoryPairs(result) {
  const pairs = []
  const add = (parent, keys) => {
    if (!parent || typeof parent !== 'object') return
    keys.forEach((key) => {
      if (parent[key] || parent[`${key}_source`]) {
        pairs.push([parent[key], parent[`${key}_source`]])
      }
    })
  }
  add(result, ['summary', 'warning'])
  ;(result?.warnings || []).forEach((row) => add(row, ['title', 'summary', 'description', 'reason', 'warning']))
  ;(result?.scenarios || []).forEach((scenario) => {
    add(scenario, ['title', 'summary', 'rationale', 'warning'])
    ;(scenario?.suggestions || []).forEach((row) => add(row, ['reason', 'alternative']))
  })
  ;(result?.synergies || []).forEach((row) => add(row, [
    'title', 'summary', 'description', 'reason', 'removal_consequence',
    'recommendation', 'recommendation_limits',
  ]))
  ;(result?.duplicates || []).forEach((row) => add(row, [
    'title', 'summary', 'description', 'reason', 'recommendation', 'warning',
  ]))
  return pairs
}

export function languageBanner(result) {
  const repair = result?.language_repair || {}
  const states = explanatoryPairs(result).map(([text, source]) => resolveExplanatoryText(text, source))
  const lostInContent = states.some((state) => state.kind === 'lost')
  const pendingInContent = states.some((state) => state.kind === 'pending')
  if (repair.originals_lost || lostInContent) {
    return {
      kind: 'lost',
      message: repair.message || ORIGINAL_LOST_FR,
      canRetry: false,
      canReanalyze: true,
    }
  }
  const unresolved = Array.isArray(repair.unresolved) ? repair.unresolved.length : 0
  if (repair.can_retry || unresolved > 0 || pendingInContent) {
    return {
      kind: 'pending',
      message: repair.message || TRANSLATION_FAILED_FR,
      canRetry: true,
      canReanalyze: false,
    }
  }
  return null
}

export const PREVIEW_FRENCH_BY_SOURCE = {
  'Keep this item because it is essential for the budget.':
    'Conserver cet article, déjà essentiel pour le budget.',
  'Keep the NVIDIA GeForce RTX 4070 SUPER because it is essential.':
    'Conserver le NVIDIA GeForce RTX 4070 SUPER car il est essentiel.',
  'Processor and motherboard pairing.':
    'Association du processeur et de la carte mère.',
  'These two items work together.':
    'Ces deux articles fonctionnent ensemble.',
  'These two accessories share the same desk.':
    'Ces deux accessoires relèvent du même poste de travail.',
  'These two accessories share the same use.':
    'Ces deux accessoires partagent la même utilisation.',
}

function translateIfKnown(value, source, map) {
  const original = String(source || value || '').trim()
  const current = String(value || '').trim()
  if (!looksLikeEnglishProse(current) && !isUnavailablePlaceholder(current)) return current
  if (map[original]) return map[original]
  if (map[current]) return map[current]
  return current
}

export function applyFixtureTranslations(result, map = PREVIEW_FRENCH_BY_SOURCE) {
  const next = {
    ...result,
    scenarios: (result?.scenarios || []).map((scenario) => ({
      ...scenario,
      suggestions: (scenario.suggestions || []).map((row) => ({
        ...row,
        reason_source: row.reason_source || row.reason,
        reason: translateIfKnown(row.reason, row.reason_source, map),
        alternative_source: row.alternative ? (row.alternative_source || row.alternative) : row.alternative_source,
        alternative: row.alternative ? translateIfKnown(row.alternative, row.alternative_source, map) : row.alternative,
      })),
    })),
    synergies: (result?.synergies || []).map((row) => ({
      ...row,
      summary_source: row.summary_source || row.summary,
      summary: translateIfKnown(row.summary, row.summary_source, map),
      reason_source: row.reason_source || row.reason,
      reason: translateIfKnown(row.reason, row.reason_source, map),
    })),
    duplicates: (result?.duplicates || []).map((row) => ({
      ...row,
      reason_source: row.reason_source || row.reason,
      reason: translateIfKnown(row.reason, row.reason_source, map),
    })),
  }
  const stillPending = [
    ...(next.scenarios || []).flatMap((scenario) => (scenario.suggestions || []).map((row) => row.reason)),
    ...(next.synergies || []).map((row) => row.summary),
    ...(next.duplicates || []).map((row) => row.reason),
  ].some((text) => looksLikeEnglishProse(text) || isUnavailablePlaceholder(text))
  next.language_repair = {
    ...(result?.language_repair || {}),
    attempted: true,
    can_retry: stillPending,
    unresolved: stillPending ? ['preview'] : [],
    originals_lost: false,
    message: stillPending ? TRANSLATION_FAILED_FR : '',
  }
  return next
}

export function synergyRelationLabel(relation) {
  if (relation === 'complementarity') return 'Complémentarité'
  if (relation === 'functional_dependency') return 'Dépendance fonctionnelle'
  if (relation === 'shared_use') return 'Usage commun'
  if (relation === 'redundancy') return 'Redondance'
  return ''
}

export function synergyCertaintyLabel(certainty) {
  if (certainty === 'established') return 'Relation établie'
  if (certainty === 'hypothesis') return 'Hypothèse à vérifier'
  return 'Hypothèse à vérifier'
}
